// External imports
import axios from 'axios';
import jwtDecode from 'jwt-decode';
// Internal imports
import WebHelpers from '~/app/webHelpers.js';
import XlinkAPI from './xlinkAPI';
import { isValidSSOHandoffOrigin, parseOriginFromDomain } from './helpers/ssoRedirectHelpers.js';
import { ACCESS_TOKEN, LOGIN_SETTINGS, REFRESH_TOKEN } from '~/app/constants.js';
import { SSO_PRE_AUTH_TOKEN_KEY } from '~/app/Pages/SSOAccountPicker/helpers/ssoConstants.js';
// Redux imports
import { store } from '~/app/redux/index';
import { actions as appActions } from '~/app/redux/modules/app';

const CancelToken = axios.CancelToken;
// each request will generate it's own cancel function
const cancelFunctions = [];

export default class AuthAPI {
  static validateOrRefresh = (fingerprint, forceRefresh) => {
    // use a different axios instance so to not interfer with the response interceptor and cause a loop
    const newAxiosInstance = axios.create();
    const payload = WebHelpers.getJWTPayload();

    return new Promise((resolve, reject) => {
      const jwt = sessionStorage.getItem(ACCESS_TOKEN);
      const refreshjwt = localStorage.getItem(REFRESH_TOKEN);
      if (!jwt) return resolve(false);
      // Check if TermsandConditions page needs to render or else T&C will be bypassed with a valid token on refresh
      if (sessionStorage.getItem(LOGIN_SETTINGS.SHOW_TERMS_AND_CONDITIONS) === 'true') {
        return resolve(false);
      }

      // retrieve the expiration field from the access_token
      const tokenExp = jwtDecode(jwt)?.exp;
      if (tokenExp <= 0) {
        return resolve(false);
      }

      const currentTime = new Date().getTime() / 1000;

      const handleRefresh = () => {
        // failed to validate, if the error is anything other than "expired" the refresh token should have been revoked
        if (!payload || !payload.identifier || payload.identifier === '') return resolve(false);
        newAxiosInstance
          .post(
            `${BASE_URL}/authn/refresh`,
            {
              refresh_token: refreshjwt,
              identifier: payload.identifier,
              fingerprint,
              season: payload.season,
              isBusiness: payload.is_business,
              isFeederOffice: payload?.is_feeder_office, // LOOK INTO THIS
            },
            WebHelpers.jsonContentType,
          )
          .then(res => {
            sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token); // 2nd Factor Authorized token via refresh
            localStorage.setItem(REFRESH_TOKEN, res.data.refresh_token);

            // retrieve the access_token expiration field and save it to the store
            const tokenExp = jwtDecode(res.data.access_token)?.exp;
            store.dispatch(appActions.updateRefreshTimer(tokenExp));

            return resolve(res);
          })
          .catch(error => {
            // Refresh token expired or invalid, will not grant access
            if (!error && !error.response) {
              WebHelpers.cleanupSession();
              WebHelpers.cleanupLocalStorage();
            }

            return reject(error);
          });
      };

      // If the expiration time is within 60 seconds, trigger refresh - skip validation request. Unless we are forcing the refresh
      if ((tokenExp - currentTime >= 0 && tokenExp - currentTime <= 60) || forceRefresh) {
        handleRefresh();

        // short circuit
      } else if (currentTime + 60 <= tokenExp) {
        return resolve(true);
      }

      // first, try to validate the existing access token
      newAxiosInstance
        .get(`${BASE_URL}/authz/validate`, WebHelpers.getAuthHeadersFor(jwt))
        .then(() => {
          return resolve(true);
        })
        .catch(() => {
          handleRefresh();
        });
    });
  };

  static toggleBusinessMode = isFeederOffice => {
    return new Promise((resolve, reject) => {
      const jwt = sessionStorage.getItem(ACCESS_TOKEN);
      if (!jwt) return resolve(false);

      const payload = WebHelpers.getJWTPayload();
      if (!payload || !payload.identifier || payload.identifier === '') return resolve(false);

      axios
        .post(
          `${BASE_URL}/authz/toggle-business-mode/${isFeederOffice}`,
          {},
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          return resolve(true);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static submitCredentials = (username, password, fingerprint) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/login`,
          {
            identifier: username,
            plaintext: password,
            fingerprint,
          },
          WebHelpers.jsonContentType,
        )
        .then(res => {
          if (res.data.password_status && res.data.password_status === 'expired') {
            return resolve(res.data);
          }
          const encodedToken = res.data.access_token;
          // ensure we can decode, store the encoded token
          const payload = WebHelpers.getJWTPayload(encodedToken);
          if (payload === '') {
            // eslint-disable-next-line prefer-promise-reject-errors
            return reject('Invalid payload');
          }
          if (payload.Identifier === '') {
            // eslint-disable-next-line prefer-promise-reject-errors
            return reject('Invalid JWT response');
          }
          sessionStorage.setItem(ACCESS_TOKEN, encodedToken);
          sessionStorage.setItem(LOGIN_SETTINGS.SHOW_IRS_MESSAGE, res.data.irsMessage);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          // Terms and conditions should always come back on log in but just in case check it and set it on session storage
          let showTandC = true;
          if (res.data?.termsAndConditions) {
            showTandC = !res.data.termsAndConditions;
          }

          sessionStorage.setItem(LOGIN_SETTINGS.SHOW_TERMS_AND_CONDITIONS, showTandC);
          sessionStorage.setItem(
            LOGIN_SETTINGS.TERMS_AGREEMENT,
            JSON.stringify(res.data?.termsAgreement ?? null),
          );

          // Check if login is of type live or demo and redirect user accordingly
          if (res.data?.demoDomain && res.data?.liveDomain) {
            const href = window.location.href.replace('www.', '');
            // checking login name for demo indicator
            const isDemoLoginName = username.startsWith('demo-');
            const isDemoDomain = href.startsWith(res.data.demoDomain);

            if (isDemoLoginName && !isDemoDomain) {
              // Demo login attempting to log in at live domain
              alert('Please use Demo URL to log into demo accounts, browser will redirect');
              window.location.href = res.data.demoDomain;
              return resolve(false);
            } else if (!isDemoLoginName && isDemoDomain) {
              // Live login on the demo URL
              // non-demo login attemping to log in at demo(or other non-live) domain
              alert('Please use Live URL to log into non-demo accounts, browser will redirect');
              window.location.href = res.data.liveDomain;
              return resolve(false);
            } else if (!isDemoLoginName && !href.startsWith(res.data.liveDomain)) {
              // Live login on the wrong URL
              alert('You have navigated to an incorrect domain, browser will redirect');
              window.location.href = res.data.liveDomain;
              return resolve(false);
            }
          }

          return resolve(true);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static submitOTPToken = async (token, fingerprint, codeMethod) => {
    return new Promise((resolve, reject) => {
      const jwt = sessionStorage.getItem(ACCESS_TOKEN);
      axios
        .post(
          `${BASE_URL}/reg/authmfa`,
          {
            OTPToken: token,
            fingerprint,
            codeMethod,
          },
          WebHelpers.getAuthHeadersFor(jwt),
        )
        .then(res => {
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token); // 2nd Factor Authorized token
          localStorage.setItem(REFRESH_TOKEN, res.data.refresh_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // temp deviceLabel  - just testing things
  static submitBypassMFA = async fingerprint => {
    return new Promise((resolve, reject) => {
      const jwt = sessionStorage.getItem(ACCESS_TOKEN);
      axios
        .post(
          `${BASE_URL}/reg/authbypassmfa`,
          {
            fingerprint,
          },
          WebHelpers.getAuthHeadersFor(jwt),
        )
        .then(res => {
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token); // 2nd Factor Authorized token
          localStorage.setItem(REFRESH_TOKEN, res.data.refresh_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static selectAnOfficeYear = async (officeID, season) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authz/switchofficeyear`, `office_id=${officeID}&season=${season}`, {
          ...WebHelpers.urlEncodedContentType(),
          ...WebHelpers.getAuthHeaders(),
        })
        .then(res => {
          // success, store the new access token
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          resolve();
        })
        .catch(error => reject(error));
    });
  };

  static toggleTrainingMode = async () => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authz/toggletrainingmode`, {}, WebHelpers.getAuthHeaders())
        .then(res => {
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          resolve();
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static impersonateLogin = async (identifier, isFeederOffice) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authz/impersonate`,
          { identifier, isFeederOffice }, // MAKE SURE THIS WORKS
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          sessionStorage.setItem(ACCESS_TOKEN, res.data.access_token);

          // retrieve the access_token expiration field and save it to the store
          const tokenExp = jwtDecode(res.data.access_token)?.exp;
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static sendCodeToEmail = username => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/forgotpassword`,
          { identifier: username },
          WebHelpers.getAuthHeaders(),
        )
        .then(resp => {
          if (resp) {
            resolve(resp);
          } else {
            resolve();
          }
        })
        .catch(error => reject(error));
    });
  };

  static sendUsernameToEmail = email => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authn/forgotusername`, { email }, WebHelpers.getAuthHeaders())
        .then(() => resolve())
        .catch(error => reject(error));
    });
  };

  static resetPassword = (username, code, plaintext) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/resetpasswordwithcode`,
          {
            identifier: username,
            code,
            plaintext,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  static changePassword = (oldpassword, newpassword) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authz/resetpassword`,
          {
            oldpassword,
            password: newpassword,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  static logout = async () => {
    await XlinkAPI.cancelAllRequests();
    await XlinkAPI.clearMyLocks();
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authz/invalidate`, WebHelpers.getAuthHeaders())
        .then(() => {
          WebHelpers.cleanupSession();
          WebHelpers.cleanupLocalStorage();
          resolve();
        })
        // eslint-disable-next-line prefer-promise-reject-errors
        .catch(() => reject());
    });
  };

  static verifyReCaptcha = token => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/verifyrecaptcha/${token}`, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static registerAccount = (account, accountType, loginID) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authz/register/${accountType}`,
          { account, drilled_login: loginID },
          WebHelpers.getAuthHeaders(),
        )
        .then(() => resolve())
        .catch(error => reject(error));
    });
  };

  static registerAccountPassword = (verifyCode, newPassword, confirmPassword) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/registerAccount`,
          {
            verifyCode,
            newPassword,
            confirmPassword,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static updateAccountActivationlink = verifyCode => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authn/updateActivationLink`, { verifyCode }, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  /*
    static checkiflinkexpired = (verifyCode) => {
        console.log("Called checkiflinkexpired on AUTHAPI")
        return new Promise((resolve, reject) => {
            axios.post(`${BASE_URL}/authn/checkLinkExpiration`, { "verifyCode": verifyCode},
                WebHelpers.getAuthHeaders())
                .then((res) => {
                    return resolve(res)
                })
                .catch((error) => {
                    return reject(error)
                });
        })
    }
    */

  static checkiflinkexpired = verifyCode => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/checkLinkExpiration/${verifyCode}`, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static getUsernameFromLink = verifyCode => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/getUsernameFromLink/${verifyCode}`, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static getQRCode = verifyCode => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authn/mfatemp`, { verifyCode }, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static generateNewQRCode = password => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authz/mfa/generate/qrcode`, { password }, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static resendAccountActivationLink = (accountID, sendLoginID, loginID, email) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authz/resend`,
          {
            account: accountID,
            send_to_login: sendLoginID,
            drilled_login: loginID,
            email,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // GetRemoteSignPDF fetches the remote-sign PDF as a blob (Safari-friendly).
  static GetRemoteSignPDF = (sigDocID, signeeType, signatureToken) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/signatures/${sigDocID}/pdf/${signeeType}`, {
          params: {
            token: signatureToken,
          },
          responseType: 'blob',
        })
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  static verifyRemoteDocRequestAuth = (
    name,
    lastFourSSNEIN,
    dob,
    requesteeType,
    authorizationCode,
    zipCode,
  ) => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${BASE_URL}/authn/VerifyRemoteDocRequest`,
          {
            params: {
              name,
              ssnEIN: lastFourSSNEIN,
              dob,
              requesteeType,
              authorizationCode,
              zipCode,
            },
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static VerifyPreparerRemoteSignature = (name, lastFourPTIN, requesteeType, authorizationCode) => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${BASE_URL}/authn/VerifyPreparerRemoteSignature`,
          {
            params: {
              name,
              lastFourPTIN,
              requesteeType,
              authorizationCode,
            },
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // Get signature for preparer from a public route
  static getSignatureForPreparer = preparerID => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/preparer/signature/${preparerID}`, {}, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // signatureString is a string containing base 64 data
  static postSignature = async (
    newSignature,
    preparerID,
    hierachyTypeID,
    signeeTypeID,
    signaturePadTypeID,
    remoteSignatureRequestID,
    createdBy,
  ) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/preparer/signature`,
          {
            newSignature,
            preparerID,
            hierachyTypeID,
            signeeTypeID,
            signaturePadTypeID,
            remoteSignatureRequestID,
            createdBy,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static CheckRemoteSignAuthData = (
    paramLastName,
    paramDOB,
    paramLastFour,
    paramZipCode,
    paramCodeRemoteSign,
    requesteeType,
    isIODSigningMethod,
  ) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/verifyRemoteSignAuthData`,
          {
            paramLastName,
            paramDOB,
            paramLastFour,
            paramZipCode,
            paramCodeRemoteSign,
            requesteeType,
            isIODSigningMethod,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // CheckRemoteSignAuthDataPrepEROBusiness
  static CheckRemoteSignAuthDataPrepEROBusiness = (
    lastName,
    lastFourPrimary,
    codeRemoteSign,
    signeeType,
    isIODSigningMethod,
  ) => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${BASE_URL}/authn/verifyRemoteSignAuthDataPrepEROBusiness/${lastName}/${lastFourPrimary}/${signeeType}/${codeRemoteSign}/${isIODSigningMethod}`,
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  // AuthAPI.PostCodeRemoteSign(documentID, typeID, signatureEmail, signatureID)
  static PostCodeRemoteSign = (sigDocID, typeID, signatureEmail, signatureValidationID) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/postremotesignaturecode`,
          {
            sigDocID,
            typeID,
            signatureEmail,
            signatureValidationID,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static postRemoteDocumentData = (remoteDocID, fileData, description, isBusiness = false) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${BASE_URL}/authn/UploadRemoteDocument/${remoteDocID}`,
          {
            file: fileData.file,
            fileName: fileData.fileName,
            fileType: fileData.fileType,
            fileSize: fileData.fileSize,
            description,
            isBusiness,
          },
          WebHelpers.getAuthHeaders(),
        )
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static PostSignedSignatureData = async (
    sigDocID,
    signatureValidationID,
    categoryID,
    typeID,
    remoteSignatureData,
    signatureToken,
    electronicDisclosureConsent,
    season,
    isIODSigningMethod,
    consent,
  ) => {
    return await axios.post(
      `${BASE_URL}/authn/signatures/${sigDocID}`,
      {
        signatureValidationID,
        categoryID,
        typeID,
        remoteSignatureData,
        signatureToken,
        electronicDisclosureConsent,
        season,
        isIODSigningMethod,
        consent,
      },
      {
        ...WebHelpers.getAuthHeaders(),
        cancelToken: new CancelToken(fn => {
          cancelFunctions.push(fn);
        }),
      },
    );
  };

  static UpsertAppVersion = version => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/pub/upsertappversion`, version, WebHelpers.getAuthHeaders())
        .then(res => {
          return resolve(res);
        })
        .catch(error => {
          return reject(error);
        });
    });
  };

  static getAppVersion = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/pub/getappversion`, {
          ...WebHelpers.getAuthHeaders(),
        })
        .then(resp => resolve(resp))
        .catch(error => reject(error));
    });
  };

  static IsRMSAuthCodeActive = async (verifyCode, signeeType) => {
    return await axios.get(
      `${BASE_URL}/authn/RMS/verify-auth-code/${signeeType}/${verifyCode}`,
      {},
      {
        ...WebHelpers.getAuthHeaders(),
        cancelToken: new CancelToken(c => {
          cancelFunctions.push(c);
        }),
      },
    );
  };

  static IsRMDAuthCodeActive = async verifyCode => {
    return await axios.get(
      `${BASE_URL}/authn/verify-doc-auth-code/${verifyCode}`,
      {},
      {
        ...WebHelpers.getAuthHeaders(),
        cancelToken: new CancelToken(c => {
          cancelFunctions.push(c);
        }),
      },
    );
  };

  /**
   * Fetches the list of CLO accounts linked to the authenticated SSO identity.
   * Sends the SSO pre-auth JWT (stored by SSOAccountPicker after the callback
   * redirect) as the Bearer token. Resolves the full Axios response object
   * (including status and data fields) on success.
   * CLO-11566
   */
  static fetchSSOAccounts = ({ page = 1, perPage = 14, search = '' } = {}) => {
    const preAuthToken = sessionStorage.getItem(SSO_PRE_AUTH_TOKEN_KEY);
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/authn/sso/accounts`, {
          headers: { Authorization: `Bearer ${preAuthToken}` },
          params: { page, perPage, search },
        })
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  /**
   * Fetches SSO identity mappings by status for the Superuser Utilities page.
   * isActive=true => active mappings, isActive=false => disabled mappings.
   */
  static getSSOMappingsByStatus = ({ isActive, page, perPage, search }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/api/sso/mappings`, {
          ...WebHelpers.getAuthHeaders(),
          params: { isActive, page, perPage, search },
        })
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  static disableSSOMapping = payload => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/api/sso/mappings/disable`, payload, WebHelpers.getAuthHeaders())
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  static activateSSOMapping = payload => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/api/sso/mappings/activate`, payload, WebHelpers.getAuthHeaders())
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };

  /**
   * Exchanges the selected CLO login identifier for a fully-authorized JWT.
   * Sends the SSO pre-auth JWT as Bearer so the backend can verify the
   * selected identifier belongs to the authenticated SSO identity.
   * Optional requestConfig allows caller-owned lifecycle control (e.g. cancelToken)
   * so navigation/unmount can safely abort in-flight login requests.
   * Mirrors the session-storage setup in submitCredentials.
   * CLO-11566
   */
  static loginAsSSO = (identifier, requestConfig = {}) => {
    const preAuthToken = sessionStorage.getItem(SSO_PRE_AUTH_TOKEN_KEY);
    return new Promise((resolve, reject) => {
      const headers = {
        Authorization: `Bearer ${preAuthToken}`,
        ...(requestConfig.headers || {}),
      };

      axios
        .post(`${BASE_URL}/authn/sso/login`, { identifier }, { ...requestConfig, headers })
        .then(res => {
          // Cross-origin handoff: backend stored tokens under an opaque code.
          // Redirect the cobrand origin to exchange the code — no JWTs in the URL.
          // CLO-11566
          if (res.data?.handoffCode) {
            const liveOrigin = parseOriginFromDomain(res.data.liveDomain);
            if (isValidSSOHandoffOrigin(liveOrigin)) {
              sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
              window.location.href = `${liveOrigin}/sso-handoff?code=${encodeURIComponent(
                res.data.handoffCode,
              )}`;
              return resolve(false);
            }

            throw new Error('Invalid SSO handoff destination');
          }

          // Same-origin case: tokens returned directly.
          const encodedToken = res.data.access_token;
          const refreshToken = res.data.refresh_token;
          if (!encodedToken || !refreshToken) {
            throw new Error('Invalid SSO login response');
          }

          const tokenExp = jwtDecode(encodedToken)?.exp;

          // Prevent stale session context (e.g. DD_TOKEN) from mixing with
          // the newly established same-origin SSO session.
          WebHelpers.cleanupSession();
          WebHelpers.cleanupLocalStorage();

          sessionStorage.setItem(ACCESS_TOKEN, encodedToken);
          localStorage.setItem(REFRESH_TOKEN, refreshToken);
          sessionStorage.setItem(LOGIN_SETTINGS.SHOW_IRS_MESSAGE, res.data.irsMessage);

          let showTandC = true;
          if (res.data?.termsAndConditions) {
            showTandC = !res.data.termsAndConditions;
          }
          sessionStorage.setItem(LOGIN_SETTINGS.SHOW_TERMS_AND_CONDITIONS, showTandC);
          store.dispatch(appActions.updateRefreshTimer(tokenExp));

          return resolve(res);
        })
        .catch(error => reject(error));
    });
  };

  /**
   * Exchanges a one-time cross-origin SSO handoff code for session tokens.
   * Optional requestConfig supports cancellation from caller effects.
   * Called by SSOHandoff when a ?code= query param is present.
   * CLO-11566
   */
  static exchangeSSOHandoffCode = (code, requestConfig = {}) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${BASE_URL}/authn/sso/handoff`, { code }, requestConfig)
        .then(res => resolve(res))
        .catch(error => reject(error));
    });
  };
}
